import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import {
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import palette from '../assets/sass/palette.scss';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { Typography, Stack, Box } from '@mui/material';
import GamePlayerInfo from './GamePlayerInfo';
import GameTeeTime from './GameTeeTime';
import { useApi } from '../services/api';
import { clearRatingCache } from '../hooks/useWaglRating';
import LockIcon from '@mui/icons-material/Lock';
const ScoreInput = memo(
  ({
    value,
    onChange,
    placeholder,
    index,
    disabled = false,
    readonly = false,
    editable = true,
  }) => {
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleChange = useCallback(
      (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (onChange) {
          onChange(index, newValue);
        }
      },
      [onChange, index]
    );
    if (readonly) {
      return (
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {value}
        </Typography>
      );
    }
    return (
      <TextField
        type="text"
        sx={{
          p: '0px !important',
          width: '60px',
          '& input ': {
            textAlign: 'center',
            fontWeight: 'bold',
          },
          ...(editable === false && {
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }),
        }}
        disabled={disabled}
        readOnly={readonly}
        name={`txt${index}`}
        placeholder={placeholder}
        value={inputValue === undefined || inputValue === null ? '' : inputValue}
        onChange={handleChange}
        variant="outlined"
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          style: { textAlign: 'center' },
        }}
      />
    );
  }
);

const GameScoringStrokePlay = ({
  game,
  onFinalise,
  users,
  isOrganiser,
  matchNumber,
  slotPlayers,
  matchGroupingType,
  teaTime,
  match,
  viewOnly = false,
}) => {
  const [gameData, setGameData] = useState(() => ({
    ...game,
    players: [],
  }));

  useEffect(() => {
    if (slotPlayers && users) {
      // Filter out null values from slotPlayers
      const validSlotPlayers = slotPlayers.filter((player) => player !== null);
      const updatedPlayers = validSlotPlayers.map((player) => {
        // Find user details by matching uid or user field
        const userDetails = users.find(
          (user) =>
            user?.uid === player?.uid ||
            user?.user === player?.uid ||
            user?.uid === player?.user ||
            user?.user === player?.user
        );
        const netScore =
          player?.grossScore !== undefined &&
          player?.grossScore !== null &&
          player?.grossScore !== ''
            ? Number(player?.grossScore) - Number(player?.handicap)
            : '';

        return {
          ...player,
          userName:
            userDetails?.userName ||
            userDetails?.username ||
            player?.userName ||
            player?.username ||
            '',
          photoURL: userDetails?.photoURL || player?.photoURL || null,
          netScore: netScore,
          handicap: player?.handicap || 0,
          waglRating: player?.waglRating || 50,
          adjust: player?.adjust || 0,
          grossScore: player?.grossScore || 0,
        };
      });
      setGameData((prevData) => ({
        ...prevData,
        players: updatedPlayers,
      }));
    }
  }, [slotPlayers, users]);
  const groupedPlayers = useMemo(() => {
    const grouped = new Map();
    gameData.players.forEach((player) => {
      const group = player.group || '';
      if (!grouped.has(group)) {
        grouped.set(group, []);
      }
      grouped.get(group).push(player);
    });
    return grouped;
  }, [gameData.players]);

  const { db } = useFirebase();
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleScoreChange = useCallback((index, value, type = 'gross') => {
    // Only update if the value is a valid number or empty
    if (value === '' || /^-?\d*$/.test(value)) {
      setGameData((prevData) => {
        const newPlayers = [...prevData.players];
        const playerToUpdate = prevData.players[index];

        const actualIndex = newPlayers.findIndex(
          (p) => p?.user === playerToUpdate?.user || p?.uid === playerToUpdate?.user
        );

        if (actualIndex === -1) return prevData;

        const player = newPlayers[actualIndex];
        const newValue = value === '' ? '' : parseInt(value);

        if (type === 'gross') {
          const grossScore = newValue;
          const adjust = Number(player.adjust || 0);
          const handicap = Number(player.handicap || 0);
          const netScore = grossScore !== '' ? Number(grossScore) + adjust - handicap : '';

          newPlayers[actualIndex] = {
            ...player,
            grossScore: newValue,
            netScore: netScore,
            adjust: adjust,
            handicap: handicap,
          };
        } else if (type === 'adjust') {
          const grossScore = Number(player.grossScore || 0);
          const adjust = newValue;
          const handicap = Number(player.handicap || 0);
          const netScore = grossScore !== '' ? grossScore + Number(adjust || 0) - handicap : '';

          newPlayers[actualIndex] = {
            ...player,
            adjust: newValue,
            netScore: netScore,
            grossScore: grossScore,
            handicap: handicap,
          };
        }
        return {
          ...prevData,
          players: newPlayers,
        };
      });
    }
  }, []);

  const cantFinalise = useMemo(() => {
    return gameData.players.some((player) => {
      const grossScore = Number(player.grossScore);
      const adjust = Number(player.adjust || 0);
      const handicap = Number(player.handicap || 0);
      const netScore = grossScore + adjust - handicap;

      return (
        player.grossScore === undefined ||
        player.grossScore === null ||
        player.grossScore === '' ||
        isNaN(grossScore) ||
        isNaN(netScore)
      );
    });
  }, [gameData.players]);

  const handleUnlock = async () => {
    try {
      // Update local state to reflect pending status
      const updatedGameData = { ...gameData };
      if (updatedGameData.matches && updatedGameData.matches[matchNumber - 1]) {
        updatedGameData.matches[matchNumber - 1].status = 'pending';
      }

      // Update database with pending status
      const gameRef = doc(db, 'games', gameData.id);
      await updateDoc(gameRef, {
        matches: updatedGameData.matches,
      });

      // Send updated status to parent
      if (onFinalise) {
        onFinalise({
          slotPlayers,
          matchNumber,
          selectedFormat: 'stroke',
          format: 'stroke',
          matchGroupingType,
          teaTime: Timestamp.fromDate(teaTime.toDate()),
          status: 'pending',
        });
      }
    } catch (error) {
      console.error('Error unlocking match:', error);
    }
  };

  const handleFinaliseGame = async () => {
    setLoading(true);
    const gameRef = doc(db, 'games', gameData.id);
    // Update the match with the latest player data
    const sortedPlayers = gameData.players.sort((a, b) => a.grossScore - b.grossScore);
    const winner = sortedPlayers[0];
    const updatedMatch = {
      ...match,
      status: 'completed',
      winner: winner,
      players: gameData.players.map((gamePlayer) => {
        // Find the corresponding player in match.players
        const matchPlayer = match.players.find(
          (p) => p?.user === gamePlayer?.user || p?.uid === gamePlayer?.user
        );

        if (matchPlayer) {
          const didWin = gamePlayer.user === winner.user;
          return {
            ...matchPlayer,
            grossScore: Number(gamePlayer.grossScore || 0),
            adjust: Number(gamePlayer.adjust || 0),
            handicap: Number(gamePlayer.handicap || 0),
            netScore: Number(gamePlayer.netScore || 0),
            didWin: didWin,
          };
        }
        return gamePlayer;
      }),
    };

    // Update the matches array with the updated match
    const updatedMatches = [...gameData.matches];
    updatedMatches[matchNumber - 1] = updatedMatch;

    // Update the game data with the new matches
    const updatedGameData = {
      ...gameData,
      matches: updatedMatches,
      status: 'completed',
    };

    await updateDoc(gameRef, updatedGameData);

    if (onFinalise) {
      if (gameData.competitive) {
        try {
          await post('/game/score-players-in-match', {
            gameId: gameData.id,
            matchNumberIndex: matchNumber - 1,
          });
          // Clear the frontend rating cache so games played count updates immediately
          clearRatingCache();
        } catch (error) {
          console.error('Error scoring players: ', error);
        }
      }
    }
    window.location.reload();
    setLoading(false);
    setShowConfirmDialog(false);
  };

  const handleFinaliseClick = () => {
    setShowConfirmDialog(true);
  };

  const getUserPhotoURL = (player) => {
    // console.log('getUserPhotoURL player:', player);
    return player.photoURL || null;
  };

  const getUserInitial = (player) => {
    // console.log('getUserInitial player:', player);
    const userName = player.userName || player.username || '';
    return userName.charAt(0)?.toUpperCase() || '';
  };

  const ScoreInputLabel = ({ label }) => {
    return (
      <Typography
        variant="body2"
        sx={{
          p: '0px !important',
          width: '60px',
          textAlign: 'center',
          mb: 1,
          color: '#4C4C4C',
        }}
      >
        {label}
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      variant="gameBox"
      sx={{
        position: 'relative',
        ...(viewOnly && { minHeight: '325px' }),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', width: '100%' }}
      >
        <Typography variant="moduleHeader" component="h5">
          {match?.status === 'completed'
            ? `Match #${matchNumber} Results`
            : isOrganiser
            ? `Record Match #${matchNumber} Results`
            : `Match #${matchNumber} Results`}
        </Typography>
        {/* Container for lock icon and tee time box, horizontally aligned */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {!viewOnly && match?.status !== 'completed' && (
            <Box
              onClick={!viewOnly ? handleUnlock : () => {}}
              sx={{
                backgroundColor: 'black',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                cursor: !viewOnly ? 'pointer' : 'default',
              }}
            >
              <LockIcon />
            </Box>
          )}

          <GameTeeTime Timestamp={Timestamp} teeTime={teaTime} />
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column' }} spacing={2} sx={{ width: '100%' }}>
        {Array.from(groupedPlayers.entries()).map(([group, players]) => (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%' }}>
            {/* <Box
            key={group}
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto',
              gap: '16px',
            }}
          > */}
            {players.map((player, groupIndex) => {
              const initial = getUserInitial(player);
              const actualIndex = gameData.players.findIndex(
                (p) => p?.user === player?.user || p?.uid === player?.user
              );
              const actualPlayer = gameData.players.find(
                (p) => p?.user === player?.user || p?.uid === player?.user
              );
              const scoredPlayer = match.players.find(
                (p) => p?.user === actualPlayer?.user || p?.uid === actualPlayer?.user
              );

              return (
                <Box
                  key={`player-${actualIndex}`}
                  sx={{
                    mb: 2,
                    py: 1,
                    backgroundColor: palette.white,
                    border: scoredPlayer?.didWin ? '4px solid #C8B359' : '0px',
                    borderRadius: '16px',
                    width: '100%',
                    maxWidth: { xs: 'unset', lg: '401px' },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{
                      justifyContent: { xs: 'space-around', lg: 'space-between' },
                      alignItems: 'center',
                      px: { xs: 0, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        paddingTop: 1,
                        justifyContent: 'center',
                        width: '100%',
                      }}
                    >
                      <GamePlayerInfo
                        player={player}
                        initial={initial}
                        index={actualIndex}
                        photoUrl={getUserPhotoURL(player)}
                        size={{ width: 80, height: 80 }}
                        winner={scoredPlayer?.didWin}
                      />
                    </Box>
                    <Stack direction="row" spacing={2}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ScoreInputLabel label="Total" />

                        <ScoreInput
                          key={`score-${actualIndex}`}
                          disabled={match.status === 'completed' || !isOrganiser}
                          editable={true}
                          readonly={match.status === 'completed'}
                          index={actualIndex}
                          placeholder=""
                          onChange={(index, value) => handleScoreChange(index, value, 'gross')}
                          value={
                            match.status === 'completed'
                              ? scoredPlayer.grossScore
                              : player.grossScore
                          }
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ScoreInputLabel label="Adjust" />
                        <ScoreInput
                          key={`adj-${actualIndex}`}
                          disabled={match?.status === 'completed' || !isOrganiser}
                          editable={true}
                          index={actualIndex}
                          placeholder=""
                          onChange={(index, value) => handleScoreChange(index, value, 'adjust')}
                          value={
                            match.status === 'completed' ? scoredPlayer.adjust : player.adjust || 0
                          }
                          readonly={match.status === 'completed'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ScoreInputLabel label="Net" />
                        <ScoreInput
                          key={`net-${actualIndex}`}
                          disabled={match?.status === 'completed' || !isOrganiser}
                          editable={false}
                          readonly={match.status === 'completed'}
                          index={actualIndex}
                          placeholder={player.netScore}
                          value={
                            match.status === 'completed'
                              ? scoredPlayer.netScore
                              : player.netScore || 0
                          }
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          // </Box>
        ))}
      </Stack>

      {gameData.matches?.[matchNumber - 1]?.status !== 'completed' ? (
        <Button
          variant="containedWagl"
          disabled={loading || cantFinalise}
          sx={{ mt: 3, width: '100%' }}
          onClick={handleFinaliseClick}
        >
          {'Confirm Result'}
        </Button>
      ) : null}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="finalise-dialog-title"
      >
        <DialogTitle id="finalise-dialog-title">Confirm Score Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit these scores?
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleFinaliseGame} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {match?.status === 'completed' && !viewOnly && (
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', opacity: 0.8 }}>
          Game has been completed, you can still update the costs for each player.
        </Typography>
      )}
    </Paper>
  );
};

export default memo(GameScoringStrokePlay);
